import { AxiosRequestConfig } from "axios";
import qs from "qs";
import { TFetcherParams, Timestamp } from "../type";
import APIResponse from "../type/apiResponse";
import Schema, { ProductStatus } from "../type/schema";
import { ConfigStore } from "./db";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
let API_TOKEN = "";
if (typeof localStorage !== "undefined") {
	API_TOKEN = localStorage.getItem("TOKEN") || "";
}

export default async function callAPI(
	path: string,
	options?: AxiosRequestConfig
) {
	if (!API_TOKEN) {
		API_TOKEN = (await ConfigStore.getItem("TOKEN"))!;
	}

	const response = await fetch(API_URL + path, {
		...options,
		method: options?.method || "GET",
		headers: {
			...options?.headers,
			Authorization: API_TOKEN,
			"Content-Type": "application/x-www-form-urlencoded"
		},
		body: options?.data ? qs.stringify(options?.data) : undefined
	});

	if (!response.ok) {
		const text = await response.text();
		throw new Error(text);
	}

	return response.json();
}

export function uploadPhoto(args: {
	dir: string;
	fileName?: string;
	ext?: string;
	base64String: string;
}): Promise<APIResponse.Files.Post["url"]> {
	return callAPI("/api/files", {
		method: "POST",
		data: args
	}).then((data) => data.url);
}

export function getTemplate(): Promise<APIResponse.Template.Get> {
	return callAPI("/api/template");
}

export function updateTemplate(
	data: string
): Promise<APIResponse.Template.Post> {
	return callAPI("/api/template", {
		method: "POST",
		data: { payload: data }
	});
}

export function uploadFile(args: {
	dir: string;
	fileName?: string;
	ext?: string;
	base64String?: string;
	buffer?: ArrayBuffer;
}): Promise<APIResponse.Files.Post> {
	return callAPI("/api/files", { method: "POST", data: args });
}

export function getCurrentUser(): Promise<APIResponse.Users.GetOne> {
	return callAPI("/api/users/me");
}

export function updateUser(
	args: Schema.User
): Promise<APIResponse.Users.UpdateOne> {
	return callAPI("/api/users", { method: "PUT", data: { payload: args } });
}

export function changePassword(args: {
	currentPassword: string;
	newPassword: string;
}): Promise<APIResponse.Users.ChangePassword> {
	return callAPI("/api/users/me/password", {
		method: "PUT",
		data: { payload: args }
	});
}

export function createDir(dirName: string) {
	return callAPI("/api/files", {
		method: "POST",
		data: { dir: dirName, isDir: true }
	});
}

export function getDomainSubscriptions() {
	return callAPI("/api/subscriptions/me?type=domain");
}

export function getWebsiteRentalSubscriptions() {
	return callAPI("/api/subscriptions/me?type=websiteRental");
}

export function getHostingSubscriptions() {
	return callAPI("/api/subscriptions/me?type=hosting");
}

export function getProducts({
	page,
	limit,
	name,
	sku
}: {
	page?: number;
	limit?: number;
	name?: string;
	sku?: string;
}): Promise<APIResponse.Products.GetWithPagination> {
	let params = `/api/products`;
	if (page) params += `?page=${page}`;
	if (limit) params += `&limit=${limit}`;
	if (name) params += `&name=${name}`;
	if (sku) params += `&sku=${sku}`;

	return callAPI(params);
}

export function getProduct(id: string): Promise<APIResponse.Products.GetOne> {
	return callAPI("/api/products/" + id);
}

export function checkStock(id: string): Promise<APIResponse.Products.CheckStock> {
	return callAPI(`/api/products/${id}/stock`);
}

export function createProduct(
	payload: Schema.Product
): Promise<APIResponse.Products.Create> {
	return callAPI("/api/products", {
		method: "POST",
		data: payload
	});
}

export function updateProduct(
	payload: Schema.Product
): Promise<APIResponse.Products.UpdateOne> {
	return callAPI("/api/products/" + payload.id, {
		method: "PUT",
		data: payload
	});
}

export function deleteProduct(id: string) {
	return callAPI("/api/products/" + id, {
		method: "DELETE"
	});
}

export function getBlogs(): Promise<APIResponse.Blogs.Get> {
	return callAPI("/api/blogs");
}

export function getBlog(id: string): Promise<APIResponse.Blogs.GetOne> {
	return callAPI("/api/blogs/" + id);
}

export function createBlog(
	payload: Schema.Blog
): Promise<APIResponse.Blogs.Create> {
	return callAPI("/api/blogs", {
		method: "POST",
		data: payload
	});
}

export function updateBlog(
	payload: Schema.Blog
): Promise<APIResponse.Blogs.UpdateOne> {
	return callAPI("/api/blogs/" + payload.id, {
		method: "PUT",
		data: payload
	});
}

export function doLogin(payload: {
	username: string;
	password: string;
	remember: boolean;
}): Promise<APIResponse.Auth.DoLogin> {
	return callAPI("/login", { method: "POST", data: payload });
}

export const doForget = {
	sendMail(payload: { email: string }): Promise<APIResponse.Auth.DoForget> {
		return callAPI("/api/auth/forget", { method: "POST", data: payload });
	},
	validateCode(payload: { code: string }) {
		return callAPI("/api/auth/forget", { method: "POST", data: payload });
	},
	changePassword(payload: {
		code: string;
		newPassword: string;
		newPasswordConfirm: string;
	}) {
		return callAPI("/api/auth/forget", { method: "POST", data: payload });
	}
};

export function doRegister(payload: {
	fullName: string;
	phone: string;
	email: string;
	password: string;
}): Promise<APIResponse.Auth.DoRegister> {
	return callAPI("/api/auth/register", { method: "POST", data: payload });
}

export function getRentals({
	page,
	limit,
	startDate,
	createdAt,
	dueDate,
	returnDate,
	status,
	isGetDeleted = false
}: TFetcherParams): Promise<APIResponse.Rentals.GetWithPagination> {
	let params = `/api/rentals`;
	if (page) params += `?page=${page}`;
	if (limit) params += `&limit=${limit}`;
	if (startDate)
		params += `&startDateBegin=${startDate.startDate}&startDateTo=${startDate.endDate}`;
	if (createdAt)
		params += `&createdAtBegin=${createdAt.startDate}&createdAtTo=${createdAt.endDate}`;
	if (dueDate)
		params += `&dueDateBegin=${dueDate.startDate}&dueDateTo=${dueDate.endDate}`;
	if (returnDate)
		params += `&returnDateBegin=${returnDate.startDate}&returnDateTo=${returnDate.endDate}`;
	if (status) params += `&status=${status}`;
	if (isGetDeleted) params += `&isDeleted=true`;

	return callAPI(params);
}

export function getRental(
	rentalId: string
): Promise<APIResponse.Rentals.GetOne> {
	return callAPI(`/api/rentals/${rentalId}`);
}

export function getRentalsByItem(
	itemId: string
): Promise<APIResponse.Rentals.GetWithPagination> {
	return callAPI(`/api/rentals?itemId=${itemId}`);
}

export function createRental(
	payload: Schema.Rental
): Promise<APIResponse.Rentals.Create> {
	return callAPI("/api/rentals", {
		method: "POST",
		data: payload
	});
}

export function updateRental(
	payload: Schema.Rental
): Promise<APIResponse.Rentals.UpdateOne> {
	return callAPI("/api/rentals/" + payload.id, {
		method: "PUT",
		data: payload
	});
}

export function softDeleteRental(rentalId: string) {
	return callAPI(`/api/rentals/${rentalId}`, {
		method: "PUT",
		data: { isDeleted: true }
	});
}

export function getItemsByProduct(
	productId: string
): Promise<APIResponse.Items.Get> {
	return callAPI(`/api/items?productId=${productId}`).then((res) => {
		return res.items;
	});
}

export function getItem(itemId: string): Promise<APIResponse.Items.GetOne> {
	return callAPI(`/api/items/${itemId}`);
}

export function getItems(): Promise<any> {
	return callAPI("/api/items");
}

export function createItem(payload: {
	id: string;
	productId: string;
	status: ProductStatus;
	note: string;
}): Promise<APIResponse.Items.Create> {
	return callAPI("/api/items", {
		method: "POST",
		data: payload
	});
}

export function updateItem(
	payload: Schema.Item
): Promise<APIResponse.Items.UpdateOne> {
	return callAPI("/api/items/" + payload.id, {
		method: "PUT",
		data: payload
	});
}

export function deleteItem(id: string): Promise<APIResponse.Items.DeleteOne> {
	return callAPI("/api/items/" + id, {
		method: "DELETE"
	});
}

export function getCustomers({
	page,
	limit,
	createdAt,
	dob,
	name,
	phone
}: TFetcherParams): Promise<APIResponse.Customers.GetWithPagination> {
	let params = `/api/customers`;
	if (page) params += `?page=${page}`;
	if (limit) params += `&limit=${limit}`;
	if (createdAt)
		params += `&createdAtBegin=${createdAt.startDate}&createdAtTo=${createdAt.endDate}`;
	if (dob) params += `&dob=${dob}`;
	if (name) params += `&name=${name}`;
	if (phone) params += `&phone=${phone}`;

	return callAPI(params);
}

export function getCustomer(
	customerId: string
): Promise<APIResponse.Customers.GetOne> {
	return callAPI("/api/customers/" + customerId);
}

export function createCustomer(
	payload: Schema.Customer
): Promise<APIResponse.Customers.Create> {
	return callAPI("/api/customers", { method: "POST", data: payload });
}

export function updateCustomer(
	payload: Schema.Customer
): Promise<APIResponse.Customers.UpdateOne> {
	return callAPI("/api/customers/" + payload.id, {
		method: "PUT",
		data: payload
	});
}

export function deleteCustomer(id: string) {
	return callAPI("/api/customers/" + id, {
		method: "DELETE"
	});
}

export function getReport(
	startDate: Timestamp,
	endDate: Timestamp
): Promise<APIResponse.Report.Get> {
	return callAPI(`/api/report?startDate=${startDate}&endDate=${endDate}`);
}
